import React, { useEffect, useState } from 'react';
import {
  VStack,
  Input,
  Select,
  Textarea,
  Text,
  Button,
  SimpleGrid,
  FormLabel,
  FormControl,
  HStack,
  useToast,
  InputGroup,
  InputLeftAddon,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  idPlan: number;
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const TIPOS_PROGRAMA = [
  'Cultural',
  'Artístico',
  'Deportivo',
  'Educativo',
  'Recreativo',
  'Folclórico',
  'Otro',
];

const FormularioPrograma: React.FC<Props> = ({ modo, idPlan, data, onGuardado, onCancelar }) => {
  const toast = useToast();

  const [nombrePrograma, setNombrePrograma] = useState<string>(data?.nombrePrograma || '');
  const [comunidad, setComunidad] = useState<string>(data?.comunidad || '');
  const [tipoProgramaCultural, setTipoProgramaCultural] = useState<string>(data?.tipoProgramaCultural || '');
  const [descripcion, setDescripcion] = useState<string>(data?.descripcion || '');
  const [responsable, setResponsable] = useState<string>(data?.responsable || '');
  const [presupuesto, setPresupuesto] = useState<string>(
    data?.presupuestoEstimado != null ? String(data.presupuestoEstimado) : ''
  );
  const [fechaInicio, setFechaInicio] = useState<string>(
    data?.fechaInicio ? data.fechaInicio.split('T')[0] : ''
  );
  const [fechaFin, setFechaFin] = useState<string>(
    data?.fechaFin ? data.fechaFin.split('T')[0] : ''
  );
  const [localidades, setLocalidades] = useState<any[]>([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axiosInstance
      .get('/cLocalidadRoutes')
      .then((res) => setLocalidades(res.data || []))
      .catch(() => setLocalidades([]));
  }, []);

  const handleGuardar = async () => {
    if (!nombrePrograma || !comunidad || !tipoProgramaCultural || !responsable || !fechaInicio || !fechaFin) {
      toast({
        title: 'Campos obligatorios',
        description: 'Completa todos los campos requeridos.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }
    if (new Date(fechaFin) < new Date(fechaInicio)) {
      toast({
        title: 'Fechas inválidas',
        description: 'La fecha fin no puede ser anterior a la fecha inicio.',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        nombrePrograma,
        comunidad,
        tipoProgramaCultural,
        descripcion,
        responsable,
        presupuestoEstimado: presupuesto !== '' ? Number(presupuesto) : null,
        fechaInicio,
        fechaFin,
      };

      if (modo === 'edit') {
        await axiosInstance.put(`/cultura/programas/${data.id}`, payload);
      } else {
        await axiosInstance.post(`/cultura/programas/plan/${idPlan}`, payload);
      }

      toast({
        title: modo === 'edit' ? 'Programa actualizado' : 'Programa registrado',
        status: 'success',
        duration: 3000,
      });
      onGuardado?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.response?.data?.message || 'Error al guardar el programa.',
        status: 'error',
        duration: 4000,
      });
    } finally {
      setGuardando(false);
    }
  };

  if (modo === 'view') {
    const fmt = (v: string) =>
      v ? new Date(v).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';
    return (
      <VStack spacing={4} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">NOMBRE DEL PROGRAMA</Text>
            <Text fontWeight="semibold">{data?.nombrePrograma}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">COMUNIDAD</Text>
            <Text>{data?.comunidad}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">TIPO DE PROGRAMA</Text>
            <Text>{data?.tipoProgramaCultural}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">RESPONSABLE</Text>
            <Text>{data?.responsable}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">FECHA INICIO</Text>
            <Text>{fmt(data?.fechaInicio)}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">FECHA FIN</Text>
            <Text>{fmt(data?.fechaFin)}</Text>
          </div>
          <div>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">PRESUPUESTO ESTIMADO</Text>
            <Text>{data?.presupuestoEstimado != null ? `$${Number(data.presupuestoEstimado).toLocaleString('es-MX', { minimumFractionDigits: 2 })}` : '—'}</Text>
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <Text fontSize="xs" color="gray.500" fontWeight="bold">DESCRIPCIÓN</Text>
            <Text>{data?.descripcion || '—'}</Text>
          </div>
        </SimpleGrid>
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Nombre del programa</FormLabel>
          <Input
            placeholder="Ej. Taller de danza tradicional"
            value={nombrePrograma}
            onChange={(e) => setNombrePrograma(e.target.value)}
          />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Comunidad</FormLabel>
          <Select
            placeholder="Selecciona comunidad"
            value={comunidad}
            onChange={(e) => setComunidad(e.target.value)}
          >
            {localidades.map((loc) => (
              <option key={loc.id} value={loc.descripcion}>
                {loc.descripcion}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Tipo de programa</FormLabel>
          <Select
            placeholder="Selecciona tipo"
            value={tipoProgramaCultural}
            onChange={(e) => setTipoProgramaCultural(e.target.value)}
          >
            {TIPOS_PROGRAMA.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Responsable</FormLabel>
          <Input
            placeholder="Nombre del responsable"
            value={responsable}
            onChange={(e) => setResponsable(e.target.value)}
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm">Presupuesto estimado</FormLabel>
          <InputGroup>
            <InputLeftAddon>$</InputLeftAddon>
            <Input
              type="number"
              min={0}
              placeholder="0.00"
              value={presupuesto}
              onChange={(e) => setPresupuesto(e.target.value)}
            />
          </InputGroup>
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha inicio</FormLabel>
          <Input type="date" value={fechaInicio} onChange={(e) => setFechaInicio(e.target.value)} />
        </FormControl>

        <FormControl isRequired>
          <FormLabel fontSize="sm">Fecha fin</FormLabel>
          <Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} />
        </FormControl>

        <FormControl gridColumn={{ md: 'span 2' }}>
          <FormLabel fontSize="sm">Descripción</FormLabel>
          <Textarea
            placeholder="Descripción del programa..."
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            rows={3}
          />
        </FormControl>
      </SimpleGrid>

      <HStack justify="flex-end" pt={2} spacing={3}>
        <Button variant="outline" onClick={onCancelar} isDisabled={guardando}>
          Cancelar
        </Button>
        <Button
          bg="#5b182b"
          color="white"
          _hover={{ bg: '#691f30' }}
          onClick={handleGuardar}
          isLoading={guardando}
          loadingText="Guardando..."
        >
          Guardar
        </Button>
      </HStack>
    </VStack>
  );
};

export default FormularioPrograma;
